<template>
  <div class="map-container">
    <div ref="map" class="map-canvas"></div>

    <!-- Detail panel. Lives inside the map so it works identically in the SPA,
         the iframe embed and the <artaround-map> widget. -->
    <aside class="art-panel" :class="{ 'art-panel--hidden': !art }" aria-live="polite">
      <template v-if="art">
        <div class="art-card bg-white rounded-lg overflow-hidden shadow-lg">
          <button
            type="button"
            class="art-panel__close"
            aria-label="Close"
            @click="close"
          >
            &times;
          </button>

          <div class="h-40 w-full bg-gray-200">
            <img
              v-if="photoUrl(art)"
              :src="photoUrl(art)"
              alt=""
              class="h-full w-full object-cover"
            />
            <div v-else class="h-full w-full flex items-center justify-center text-gray-500">
              No Image
            </div>
          </div>

          <div class="p-4 text-left">
            <h3 class="font-bold text-lg mb-2 leading-tight">{{ art.title || "Untitled" }}</h3>
            <dl class="art-meta text-xs text-gray-600 leading-relaxed">
              <div v-if="art.artists && art.artists.length">
                <dt>Artist</dt>
                <dd>{{ art.artists.map(a => a.name).join(", ") }}</dd>
              </div>
              <div v-if="art.city">
                <dt>Location</dt>
                <dd>{{ art.city }}</dd>
              </div>
              <div v-if="art.year">
                <dt>Date</dt>
                <dd>{{ art.year }}</dd>
              </div>
              <div v-if="art.categories && art.categories.length">
                <dt>Category</dt>
                <dd>{{ art.categories.join(", ") }}</dd>
              </div>
            </dl>
            <p
              v-if="art.description"
              class="text-sm text-gray-600 mt-3"
              style="display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;"
            >
              {{ art.description }}
            </p>

            <div class="flex justify-between items-center gap-2 mt-4">
              <a
                v-if="directionsUrl(art)"
                :href="directionsUrl(art)"
                target="_blank"
                rel="noopener"
                class="flex-1 px-3 py-2 bg-white border border-gray-300 rounded text-xs font-semibold text-gray-700 hover:bg-gray-50 text-center"
              >
                Get Directions
              </a>
              <button
                type="button"
                @click="handleRedirect(art.friendly_id || art.id)"
                class="flex-1 px-3 py-2 bg-black border border-transparent rounded text-xs font-semibold text-white hover:bg-gray-800"
              >
                View Details
              </button>
            </div>
          </div>
        </div>

        <template v-if="nearby.length">
          <h4 class="art-panel__section">More Nearby Art</h4>
          <div class="art-card bg-white rounded-lg overflow-hidden shadow-lg">
            <button
              v-for="{ item, km } in nearby"
              :key="item.id"
              type="button"
              class="art-nearby"
              @click="goTo(item)"
            >
              <img v-if="pinPhotoUrl(item)" :src="pinPhotoUrl(item)" alt="" loading="lazy" />
              <span v-else class="art-nearby__blank"></span>
              <span class="art-nearby__text">
                {{ item.title || "Untitled" }}
                <span class="art-nearby__distance">{{ formatDistance(km) }} away</span>
              </span>
            </button>
          </div>
        </template>
      </template>
    </aside>
  </div>
</template>

<script>
import L from "../features/map/leaflet"
import {
  LABELS_ATTRIBUTION,
  LABELS_OPACITY,
  LABELS_PANE,
  LABELS_PANE_Z_INDEX,
  LABELS_SUBDOMAINS,
  LABELS_TILE_URL,
  USER_LOCATION_ACCURACY_STYLE,
  USER_LOCATION_DOT_STYLE,
  USER_LOCATION_PANE,
  USER_LOCATION_PANE_Z_INDEX,
  USER_LOCATION_ZOOM,
  WATERCOLOR_ATTRIBUTION,
  WATERCOLOR_MAX_ZOOM,
  WATERCOLOR_MIN_ZOOM,
  WATERCOLOR_TILE_URL,
} from "../features/map/basemap"
import {
  LOCATE_OPTIONS,
  LOCATE_TRIGGER,
  isFatalLocateError,
  locateErrorMessage,
  shouldAutoLocate,
  shouldRecenter,
  shouldReportLocateError,
} from "../features/map/geolocate"
import { LABELS_STORAGE_KEY, resolveLabels } from "../features/map/labels"
import { HOME_BOUNDS, MAX_FIT_ZOOM, boundsFor } from "../features/map/bounds"
import { directionsUrl } from "../features/map/directions"
import { clusterHtml, iconKindFor, pinHtml } from "../features/map/markers"
import { formatDistance, nearbyTo } from "../features/map/nearby"
import { primaryPhotoPinUrl, primaryPhotoUrl } from "../features/map/photos"

// The two PNG pins are the fallback for art without a photo, coloured by the
// same rule as the thumbnail ring: red for live art, gray for archived.
//
// Resolved lazily, never at module scope: file-loader bakes the URL from
// __webpack_public_path__ when the asset module first executes, and
// packs/map_widget.js rewrites that path at runtime AFTER imports have run.
// A module-level require() would freeze the build-time path and every widget
// icon would 404 on a third-party page (pinned by widget-marker-data.spec.mjs).
const iconUrlFor = kind =>
  kind === "gray" ? require("../assets/gray.png") : require("../assets/red.png")

const pngIcon = kind =>
  L.icon({
    iconUrl: iconUrlFor(kind),
    iconSize: [30, 45],
    iconAnchor: [15, 45],
  })

const photoIcon = (kind, url, selected, hot) =>
  L.divIcon({
    html: pinHtml(kind, url, selected, hot),
    className: "",
    iconSize: [44, 44],
    iconAnchor: [22, 49],
  })

// A cluster shows the first photo found among its members plus the count.
// getAllChildMarkers() walks the subtree; stopping at the first hit keeps it
// cheap even for the 800-art bubbles at the coverage zooms.
const clusterIcon = cluster => {
  let photo = null
  for (const marker of cluster.getAllChildMarkers()) {
    photo = primaryPhotoPinUrl(marker.options.item)
    if (photo) break
  }
  return L.divIcon({
    html: clusterHtml(cluster.getChildCount(), photo),
    className: "",
    iconSize: [48, 48],
    iconAnchor: [24, 24],
  })
}

// Zoom to land on when jumping to a nearby art from the panel.
const NEARBY_ZOOM = 15

// See the markerClusterGroup options in mounted(). At the last zoom level
// nothing merges except artworks on the exact same point, which markercluster
// spiderfies on click -- there is no further zoom to separate them with.
const CLUSTER_RADIUS = 30
const clusterRadiusAt = zoom => (zoom >= WATERCOLOR_MAX_ZOOM ? 1 : CLUSTER_RADIUS)

export default {
  name: "MapComponent",
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    center: {
      type: Object,
      default: () => ({ lat: 41.8781136, lng: -87.6297982 }),
    },
    zoom: {
      type: Number,
      default: 10,
    },
    // Set by a parent whose list is hovered, so the matching pin lifts.
    highlightedId: {
      type: [Number, String],
      default: null,
    },
    // Whether the street-label overlay starts on. Only the DEFAULT: a visitor
    // who has used the toggle on this origin gets their own choice instead.
    // On by default because the watercolor tiles carry no street names, and a
    // map with none was the complaint this layer answers.
    labels: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      art: null,
      nearby: [],
      // Restored from the previous visit, exactly like HomePage's showMap.
      // Storage can throw outright -- Safari in private mode, a browser set to
      // block site data -- and returns null when nothing is stored, so both
      // paths fall back to the default this map was mounted with.
      labelsOn: (() => {
        let stored = null
        try {
          stored = window.localStorage.getItem(LABELS_STORAGE_KEY)
        } catch (e) {
          // Not worth surfacing: the map still renders, it just opens on the
          // authored default rather than on what this visitor last chose.
        }
        return resolveLabels(this.labels, stored)
      })(),
      // Whether the visitor's own position is being watched right now.
      //
      // Always starts false, and deliberately is NOT restored from storage the
      // way labelsOn is: a remembered "it was on last time" is a claim about a
      // past visit, and acting on it would fire a geolocation prompt at someone
      // who has asked for nothing on this one -- on a partner's page, inside an
      // <artaround-map> they did not choose to embed.
      //
      // It can still turn true without a press, but only after the browser has
      // been asked -- silently -- whether this visitor already granted this
      // origin permission; see probeLocationPermission.
      locating: false,
      // The last locationerror, already turned into a sentence, or null. Kept
      // as plain state next to locating because the two together are the whole
      // of what the button has to show.
      locationError: null,
    }
  },
  mounted() {
    // Leaflet objects are deliberately NOT in data(): Vue 2 would walk and
    // proxy every property of the map, which is both slow and wrong.
    // Parents may mount the map before their data arrives, with center still
    // {}; Leaflet throws on an undefined LatLng, so fall back to the default.
    this.map = L.map(this.$refs.map, {
      center: this.latLngOf(this.center),
      zoom: this.zoom,
      minZoom: WATERCOLOR_MIN_ZOOM,
      maxZoom: WATERCOLOR_MAX_ZOOM,
      zoomControl: true,
      attributionControl: true,
      // Half steps let fitBounds land between integer zooms: the home view
      // (Americas + Europe) fits at z2.5 where z2 drags in Africa and India
      // and z3 cuts Europe off. Tiles are scaled by 1.41 at most, which
      // watercolor tolerates better than any other style would.
      zoomSnap: 0.5,
    })

    L.tileLayer(WATERCOLOR_TILE_URL, {
      minZoom: WATERCOLOR_MIN_ZOOM,
      maxZoom: WATERCOLOR_MAX_ZOOM,
      tileSize: 256,
      attribution: WATERCOLOR_ATTRIBUTION,
      // Off on purpose: the museum sends no CORS header, and a plain <img>
      // does not need one. Turning this on would break every tile.
      crossOrigin: false,
    }).addTo(this.map)

    this.buildLabelsLayer()
    this.buildLabelsControl()
    this.applyLabels()

    // The pane exists from the start, the layers do not: they need a position,
    // and there is no position until one is asked for. Nothing is located here
    // directly -- probeLocationPermission decides that, and only ever answers
    // yes for a permission this visitor already granted.
    //
    // Fixes handled since tracking last started, and how that tracking began.
    // Neither is in data(): the count changes on every GPS callback, nothing
    // renders from either, and shouldRecenter/shouldReportLocateError are the
    // only things that read them. Set before the control, which paints itself
    // from this state the moment it is added, and before the probe, which can
    // start a watch that reads both.
    this.locationFixes = 0
    this.locateTrigger = null
    // Set the first time the visitor presses the button to turn the dot OFF.
    // An explicit dismissal has to outlast a permission grant arriving
    // afterwards: without this, answering the browser's prompt would put back a
    // dot they had just taken away.
    this.locateDismissed = false
    this.buildUserLocationPane()
    this.buildLocateControl()
    this.probeLocationPermission()

    this.pngIcons = { red: pngIcon("red"), gray: pngIcon("gray") }
    this.markersById = new Map()

    // Clustering happens inside the layer; only visible, unclustered pins ever
    // become DOM -- and only those fetch a thumbnail. This is the fix for the
    // 2,000+ marker components the Google version mounted on the main thread.
    this.cluster = L.markerClusterGroup({
      // Pixels within which pins merge. Leaflet.markercluster defaults to 80,
      // which swallows most of a city into a handful of bubbles at the zoom a
      // city search opens on. 40 was tuned on the proof of concept: dense
      // blocks still cluster, everything else shows as real art.
      maxClusterRadius: clusterRadiusAt,
      iconCreateFunction: clusterIcon,
      chunkedLoading: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      spiderfyOnMaxZoom: true,
    })
    this.map.addLayer(this.cluster)
    this.map.on("click", this.close)
    // Parents that show "what is in view" listen to this.
    this.map.on("moveend", this.emitBounds)
    this.emitBounds()

    this.renderMarkers(this.items)

    // Leaflet measures its container once, at creation. The layouts around it
    // resize it afterwards (full-bleed map, sticky panel, a docked map that
    // expands), and a stale size means tiles stop short of the edge. Follow
    // the container itself rather than only window resizes.
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => {
        if (!this.map) return
        this.map.invalidateSize()
        // A map that was display:none emitted empty bounds; correct them now.
        this.emitBounds()
      })
      this.resizeObserver.observe(this.$refs.map)
    } else {
      this.onResize = () => this.map && this.map.invalidateSize()
      window.addEventListener("resize", this.onResize)
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.onResize) window.removeEventListener("resize", this.onResize)
    // Before map.remove(), and unconditionally rather than only when
    // this.locating says so.
    //
    // Before, because stopLocateTracking needs the map that is about to be
    // destroyed: it calls stopLocate() and off() on it, and detaches the two
    // layers.
    //
    // Leaflet's own remove() does end the watch -- `if (this._locationWatchId
    // !== undefined) { this.stopLocate(); }`, leaflet-src.js:3874 -- so the
    // watch itself is not what this call is protecting. It is here because
    // this component owns state Leaflet knows nothing about (the dot and ring
    // refs, the button, `locating`), and because the teardown must not depend
    // on the map being destroyed in the same breath. The SPA unmounts this
    // component on every navigation; a watch that outlived one would be a GPS
    // radio kept awake and a location indicator burning in the browser chrome
    // with nothing on screen to explain it.
    this.stopLocateTracking()
    // Removed by hand, and separately from the watch, because it is a separate
    // registration with a separate owner: PermissionStatus is a live object
    // navigator.permissions handed us, not something Vue proxies or Leaflet
    // knows about, so map.remove() does not touch it and nothing else will drop
    // the reference. The listener closes over `this`, so a leaked one pins the
    // whole destroyed component -- and would call startLocate on it -- every
    // time the visitor changes a site permission, once per SPA navigation for
    // the life of the tab.
    if (this.locationPermissionStatus && this.onLocationPermissionChange) {
      this.locationPermissionStatus.removeEventListener(
        "change",
        this.onLocationPermissionChange
      )
    }
    this.locationPermissionStatus = null
    this.onLocationPermissionChange = null
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    photoUrl: primaryPhotoUrl,
    pinPhotoUrl: primaryPhotoPinUrl,
    formatDistance,
    directionsUrl,
    // ---- street labels ------------------------------------------------
    buildLabelsLayer() {
      // Its own pane rather than overlayPane: the stacking order and the
      // knocked-back opacity are then decided once, here, instead of leaking
      // into per-layer options. See LABELS_PANE_Z_INDEX for the number.
      this.map.createPane(LABELS_PANE)
      const pane = this.map.getPane(LABELS_PANE)
      pane.style.zIndex = LABELS_PANE_Z_INDEX
      pane.style.opacity = LABELS_OPACITY
      // The labels are scenery painted over the basemap; nothing in them is
      // clickable. Without this the topmost tile under the cursor would be a
      // label tile, and a drag started on a street name would be a drag on an
      // <img> rather than on the map.
      pane.style.pointerEvents = "none"

      this.labelsLayer = L.tileLayer(LABELS_TILE_URL, {
        pane: LABELS_PANE,
        subdomains: LABELS_SUBDOMAINS,
        // Locked to the watercolor's own range so the two layers always cover
        // the same zooms: labels floating over blank paper, or paper with no
        // labels, both read as a broken map rather than as a style.
        minZoom: WATERCOLOR_MIN_ZOOM,
        maxZoom: WATERCOLOR_MAX_ZOOM,
        tileSize: 256,
        attribution: LABELS_ATTRIBUTION,
      })
    },
    buildLabelsControl() {
      const control = L.control({ position: "topleft" })

      control.onAdd = () => {
        const container = L.DomUtil.create("div", "aa-labels-control")
        const button = L.DomUtil.create("button", "aa-labels-toggle", container)
        button.type = "button"
        // Visible text, not an icon: this is the only piece of map chrome we
        // add, and "Labels" needs no legend. It doubles as the accessible name.
        button.textContent = "Labels"

        // Same problem the markers solve with bubblingMouseEvents: false -- a
        // click that reaches the map closes the detail panel, so pressing this
        // button would dismiss the artwork the visitor is reading.
        L.DomEvent.disableClickPropagation(container)
        L.DomEvent.disableScrollPropagation(container)
        L.DomEvent.on(button, "click", L.DomEvent.stop)
        L.DomEvent.on(button, "click", this.toggleLabels, this)

        this.labelsButton = button
        this.syncLabelsButton()
        return container
      }

      this.labelsControl = control.addTo(this.map)
    },
    toggleLabels() {
      this.labelsOn = !this.labelsOn
      // Persisted here rather than in the watcher on purpose: only a press is
      // the visitor's own choice. A host page rewriting the `labels` attribute
      // changes what is on screen without overwriting what this visitor asked
      // for the last time they touched the button.
      try {
        window.localStorage.setItem(LABELS_STORAGE_KEY, String(this.labelsOn))
      } catch (e) {
        // Not worth surfacing: the toggle still works for this visit, it just
        // will not be remembered for the next one.
      }
    },
    applyLabels() {
      if (!this.map || !this.labelsLayer) return
      // Added and removed rather than faded: an invisible layer would keep
      // requesting tiles, and Leaflet would keep crediting CARTO in the
      // attribution bar for tiles nobody is looking at.
      if (this.labelsOn) this.labelsLayer.addTo(this.map)
      else this.map.removeLayer(this.labelsLayer)
      this.syncLabelsButton()
    },
    syncLabelsButton() {
      if (!this.labelsButton) return
      // aria-pressed is what makes this a toggle rather than an action for a
      // screen reader; the class is only how it looks.
      this.labelsButton.setAttribute("aria-pressed", String(this.labelsOn))
      this.labelsButton.title = this.labelsOn
        ? "Hide street labels"
        : "Show street labels"
      L.DomUtil[this.labelsOn ? "addClass" : "removeClass"](
        this.labelsButton,
        "aa-labels-toggle--on"
      )
    },
    // ---- the visitor's own position -------------------------------------
    buildUserLocationPane() {
      // Same reasoning as the labels pane: one number decides where the dot
      // sits in the stack, and it lives with the other stacking decisions in
      // basemap.js. See USER_LOCATION_PANE_Z_INDEX for why 450.
      this.map.createPane(USER_LOCATION_PANE)
      this.map.getPane(USER_LOCATION_PANE).style.zIndex = USER_LOCATION_PANE_Z_INDEX
      // The dot reports where the visitor is; it is not something to click.
      // Without this the accuracy ring -- which at a poor fix covers a good
      // part of the screen -- would be an SVG surface swallowing every click
      // and drag aimed at the art underneath it. The layers also set
      // interactive: false, and both are needed: that option stops Leaflet
      // attaching handlers, this stops the browser routing the pointer here in
      // the first place.
      this.map.getPane(USER_LOCATION_PANE).style.pointerEvents = "none"
    },
    buildLocateControl() {
      // topleft, under the labels pill, because these are the same kind of
      // thing: small pieces of map chrome the visitor reaches for occasionally.
      const control = L.control({ position: "topleft" })

      control.onAdd = () => {
        const container = L.DomUtil.create("div", "aa-locate-control")
        const button = L.DomUtil.create("button", "aa-locate-toggle", container)
        button.type = "button"
        // Visible text rather than a crosshair glyph, matching the labels
        // pill, and it doubles as the accessible name. Worth spelling out as
        // "My location" instead of "Locate": the title changes with state
        // ("Show my location" / "Hide my location") and an accessible name has
        // to stay inside the labels a visitor might speak to a voice control
        // (WCAG 2.5.3), which a bare verb would not.
        button.textContent = "My location"

        // Identical to the labels control, and for the identical reason: the
        // map's own click handler closes the detail panel, so a press that
        // reached the map would dismiss the artwork the visitor is reading.
        L.DomEvent.disableClickPropagation(container)
        L.DomEvent.disableScrollPropagation(container)
        L.DomEvent.on(button, "click", L.DomEvent.stop)
        L.DomEvent.on(button, "click", this.toggleLocate, this)

        // Errors reach a sighted visitor through the button's colour and its
        // title. Neither exists for a screen reader until the pointer or focus
        // returns to the button, so denying permission would answer a press
        // with silence -- the visitor is left waiting for a dot that is never
        // coming (WCAG 4.1.3, and 3.3.1: an error has to be identified in
        // text, not only by colour).
        //
        // polite and not assertive: this is the answer to something they just
        // asked for, not an emergency, and it must not cut across whatever the
        // reader is in the middle of. Empty at first so nothing is announced on
        // mount; syncLocateButton fills it only when the message changes.
        const status = L.DomUtil.create("span", "aa-visually-hidden", container)
        status.setAttribute("role", "status")
        status.setAttribute("aria-live", "polite")

        this.locateButton = button
        this.locateStatus = status
        this.syncLocateButton()
        return container
      }

      this.locateControl = control.addTo(this.map)
    },
    toggleLocate() {
      // The only place LOCATE_TRIGGER.PRESS is issued, and the only place
      // locateDismissed moves -- both directions of this toggle are the visitor
      // stating an intent, and the rest of the component reads that intent back
      // rather than guessing at it. A press that turns tracking back ON after
      // an automatic session is still a press: it asks "where am I", so it
      // recenters, exactly as the very first press of a visit would.
      if (this.locating) {
        this.locateDismissed = true
        this.stopLocateTracking()
      } else {
        this.locateDismissed = false
        this.startLocate(LOCATE_TRIGGER.PRESS)
      }
    },
    startLocate(trigger) {
      if (!this.map || this.locating) return

      this.locating = true
      // Recorded before anything can call back. Leaflet dispatches
      // locationfound/locationerror on the browser's schedule and both handlers
      // read this, so a trigger set after map.locate() would be a window in
      // which an automatic session looks like a press -- and recenters.
      this.locateTrigger = trigger
      // A fresh session is a fresh question, so the previous answer --
      // including a stale "unavailable right now" from a tunnel the visitor has
      // since walked out of -- must not be what the button still says. Cleared
      // for an automatic start too: an old message left standing next to a dot
      // that is now on screen contradicts itself.
      this.locationError = null
      // Reset, not merely initialised: shouldRecenter's contract is "fixes
      // handled since tracking started". A visitor who has panned away and
      // pressed the button again is asking to be taken back, and a counter
      // carried over from the last session would refuse.
      this.locationFixes = 0

      // Bound per session rather than once in mounted(), because stopping is
      // not the same as unbinding: map.stopLocate() calls clearWatch and sets
      // _locateOptions.setView = false, and touches no listeners at all
      // (leaflet 1.9.4). clearWatch only guarantees no FURTHER callbacks; a
      // position the browser had already dispatched still arrives. Unbinding
      // here is what stops that last one repainting a dot the visitor has
      // just dismissed.
      this.map.on("locationfound", this.onLocationFound)
      this.map.on("locationerror", this.onLocationError)
      this.map.locate(LOCATE_OPTIONS)

      this.syncLocateButton()
    },
    stopLocateTracking() {
      if (!this.map) return

      // What actually ends the browser watch. Removing the layers or hiding the
      // map do not -- they are ours, the watch is the browser's -- so this is
      // the only exit that matters on the paths where the map survives.
      // (map.remove() ends one too; see the note in beforeDestroy.)
      //
      // Safe to call when nothing was ever started: Leaflet guards on
      // navigator.geolocation, and clearWatch ignores an id it does not know,
      // which is what lets beforeDestroy call this unconditionally rather than
      // trusting `locating` to be in sync.
      this.map.stopLocate()
      this.map.off("locationfound", this.onLocationFound)
      this.map.off("locationerror", this.onLocationError)

      this.removeUserLocationLayers()
      this.locating = false
      this.syncLocateButton()
    },
    removeUserLocationLayers() {
      for (const layer of [this.userAccuracyRing, this.userDot]) {
        if (layer && this.map) this.map.removeLayer(layer)
      }
      // Dropped rather than kept for reuse, so the next press cannot flash the
      // position from the previous one for the seconds before the first fix
      // arrives. That stale dot would be a confident lie about where the
      // visitor is standing.
      this.userAccuracyRing = null
      this.userDot = null
    },
    onLocationFound(e) {
      if (!this.map) return
      // A fix is the answer to whatever the last error was, including the
      // transient ones we kept watching through.
      this.locationError = null

      // Metres, straight from the GeolocationCoordinates the browser handed
      // Leaflet (Map._handleGeolocationResponse copies every numeric field of
      // pos.coords onto the event).
      //
      // Guarded anyway, and specifically to 0 rather than to some assumed
      // default. A non-finite radius does not throw in Leaflet 1.9.4 --
      // Circle._project ends in `this._radius = isNaN(lngR) ? 0 : ...`, so it
      // clamps to zero on its own -- but it projects the ring's centre through
      // a NaN latitude on the way there, which puts the shape nowhere in
      // particular. Zero gives the same invisible ring at the RIGHT place, so
      // the next fix updates it into view instead of moving it back from limbo.
      const accuracy = Number.isFinite(e.accuracy) ? e.accuracy : 0

      if (this.userDot) {
        this.userAccuracyRing.setLatLng(e.latlng)
        this.userAccuracyRing.setRadius(accuracy)
        this.userDot.setLatLng(e.latlng)
      } else {
        // The ring is added first so the dot draws over it: both live in the
        // same pane, where SVG paints in insertion order and z-index cannot
        // separate two shapes that share a parent.
        //
        // L.circle for the ring (radius in METRES, so it scales with zoom --
        // that is the entire claim it makes) and L.circleMarker for the dot
        // (radius in PIXELS, so "you" stays the same size at every zoom).
        // Swapping the two gives a dot that swells as you zoom in and a ring
        // that asserts a different accuracy at every zoom level.
        this.userAccuracyRing = L.circle(e.latlng, {
          pane: USER_LOCATION_PANE,
          radius: accuracy,
          ...USER_LOCATION_ACCURACY_STYLE,
        }).addTo(this.map)
        this.userDot = L.circleMarker(e.latlng, {
          pane: USER_LOCATION_PANE,
          ...USER_LOCATION_DOT_STYLE,
        }).addTo(this.map)
      }

      if (shouldRecenter(this.locationFixes, this.locateTrigger)) {
        // setView and not flyTo (which goTo uses for the nearby list): the map
        // may be on the world view, and flying there is a multi-second
        // animation over a question the visitor asked expecting an answer now.
        //
        // Zoom is raised, never lowered. A visitor already closer than
        // USER_LOCATION_ZOOM has chosen that zoom, and pulling them back out to
        // a fixed level would undo a decision they made on purpose.
        this.map.setView(e.latlng, Math.max(this.map.getZoom(), USER_LOCATION_ZOOM))
      }
      this.locationFixes += 1

      this.syncLocateButton()
    },
    onLocationError(e) {
      // Quiet by contract: no alert, no thrown error, no retry loop. A visitor
      // who says no has said no, and the map behind this button works exactly
      // as well without a dot on it. All that changes is what the button says.
      //
      // And for a watch that started on its own, not even that. Nobody pressed
      // anything, so there is no question outstanding and nothing to report:
      // putting "Location blocked" on an untouched button -- on a partner's
      // page, in red, announced to a screen reader -- would be the map raising
      // a problem the visitor neither caused nor asked about. The failure is
      // still ACTED on below; it just is not narrated. See
      // shouldReportLocateError for why this defaults the other way round from
      // shouldRecenter.
      if (shouldReportLocateError(this.locateTrigger)) {
        this.locationError = locateErrorMessage(e && e.code)
      }

      // Independent of the above on purpose. A denial is permanent for this
      // visit -- the browser will not prompt again -- so the watch behind it
      // can only sit there holding a permission indicator open, and that is
      // just as true of a watch nobody started (a visitor revoking permission
      // mid-visit lands exactly here). The transient failures (a tunnel, a slow
      // first fix) are left alone: the same watch recovers from those on a
      // later callback, and tearing it down would turn a two-second glitch into
      // a dead button.
      if (isFatalLocateError(e && e.code)) this.stopLocateTracking()
      else this.syncLocateButton()
    },
    syncLocateButton() {
      if (!this.locateButton) return

      // Tracks the watch, not the press. aria-pressed on a toggle states
      // whether the thing is currently on, so a watch that started on its own
      // must read "pressed" too -- otherwise the first thing the button tells a
      // screen reader user is that showing their location is off while their
      // location is on the map, and their press would then read as turning it
      // ON while it actually turns it off.
      //
      // True while the first fix is still being acquired as well: the state is
      // live from the moment the watch starts, and reporting "not pressed" for
      // the ten seconds a cold GPS chip takes would contradict the thing that
      // just happened.
      this.locateButton.setAttribute("aria-pressed", String(this.locating))

      // Precedence is what the button owes the visitor an explanation for.
      // An error outranks everything -- it is the only state they cannot infer
      // from the map. Then "still looking", because high accuracy can take
      // seconds and a button that looks finished while nothing appears reads as
      // broken. Then the plain on/off.
      const pending = this.locating && this.locationFixes === 0
      this.locateButton.title = this.locationError
        ? this.locationError
        : pending
          ? "Finding your location…"
          : this.locating
            ? "Hide my location"
            : "Show my location"

      // Classes are only how it looks; aria-pressed and the title above are
      // what a screen reader gets. Set explicitly in both directions because
      // this method runs on every state change, not just on the way in.
      L.DomUtil[this.locating ? "addClass" : "removeClass"](
        this.locateButton,
        "aa-locate-toggle--on"
      )
      L.DomUtil[pending ? "addClass" : "removeClass"](
        this.locateButton,
        "aa-locate-toggle--pending"
      )
      L.DomUtil[this.locationError ? "addClass" : "removeClass"](
        this.locateButton,
        "aa-locate-toggle--error"
      )

      // Only the error is announced. "Finding your location" and the on/off
      // states are already legible from aria-pressed and from the dot itself,
      // and a live region that narrates every transition is one a visitor
      // learns to tune out -- which would cost us the one announcement that
      // actually matters.
      //
      // Written only on change: assigning the same string again re-announces
      // it in several screen readers, so a transient error arriving twice
      // would be read twice.
      if (this.locateStatus) {
        const message = this.locationError || ""
        if (this.locateStatus.textContent !== message) {
          this.locateStatus.textContent = message
        }
      }
    },
    probeLocationPermission() {
      // The one question that can be asked about geolocation without asking FOR
      // geolocation: navigator.permissions.query({name: 'geolocation'}) reports
      // the state and raises no dialog, where getCurrentPosition/watchPosition
      // would. That is the entire reason a watch may start without a press.
      //
      // It matters because this component is not only the site's map. It is
      // also the embedded map partners put on their own pages (EmbedMap.vue,
      // packs/map_widget.js), where nothing has ever asked the visitor for
      // anything, and where a permission dialog fired by a third-party widget
      // is the one outcome this design must never produce. So the only state
      // acted on is 'granted' -- consent this visitor already gave this origin.
      const permissions = window.navigator && window.navigator.permissions
      // Not a failure, and not worth reporting: the visitor cannot act on it
      // and nothing is broken. Every dead end in this method means the same
      // thing -- do not auto-start, leave the button exactly as it was, and the
      // feature is the press-to-start one it was before.
      if (!permissions || typeof permissions.query !== "function") return

      let query
      try {
        query = permissions.query({ name: "geolocation" })
      } catch (e) {
        // Belt and braces, and worth being honest about which: the spec says an
        // unsupported permission name REJECTS, which the .catch below handles,
        // so on a conforming engine this branch is dead. It is here because of
        // what it guards rather than because of a bug anyone can point at -- an
        // uncaught throw would abort the rest of mounted(), taking the cluster
        // layer, the ResizeObserver and emitBounds with it, and this whole probe
        // is an optional nicety. A feature that can turn itself on must never be
        // able to break the map by failing to.
        return
      }
      // Guarded rather than assumed thenable, because the whole call is a
      // best-effort probe of an API we have just established may not exist in
      // the shape we expect.
      if (!query || typeof query.then !== "function") return

      query
        .then((status) => this.adoptLocationPermission(status))
        // The specified failure path for an unsupported permission name.
        // Swallowed deliberately: an unanswerable question is the same answer
        // as 'prompt' -- press the button to start.
        .catch(() => {})
    },
    adoptLocationPermission(status) {
      // The promise resolves a tick or more after mounted(), and the SPA can
      // unmount this component inside that window. beforeDestroy has then
      // already run, so there is nothing left to start a watch on and nobody
      // left to remove a listener afterwards.
      if (!this.map || !status) return

      if (shouldAutoLocate(status.state)) this.startLocate(LOCATE_TRIGGER.AUTO)

      // 'prompt' is not a no, it is "not yet". On the main site a first-time
      // visitor is being asked by something else entirely at roughly this
      // moment: HomePage.vue:418 calls fetchUserLocation() unconditionally on
      // load, which is a bare navigator.geolocation.getCurrentPosition. So the
      // dialog on screen is that one, and PermissionStatus fires `change` when
      // they answer it. That event is the only way to learn a grant happened
      // without asking for one ourselves, so it is subscribed to in every state
      // -- 'denied' can become 'granted' too, when a visitor fixes it in site
      // settings mid-visit.
      if (typeof status.addEventListener !== "function") return
      this.locationPermissionStatus = status
      this.onLocationPermissionChange = () => {
        // locateDismissed is the reason this is not simply "start if granted".
        // A visitor who pressed the button to hide the dot has said no to it;
        // a permission grant answered afterwards must not be read as taking
        // that back.
        if (!this.map || this.locating || this.locateDismissed) return
        if (shouldAutoLocate(status.state)) this.startLocate(LOCATE_TRIGGER.AUTO)
      }
      // Removed in beforeDestroy. Nothing else can: navigator.permissions owns
      // this object and outlives every component that reads it.
      status.addEventListener("change", this.onLocationPermissionChange)
    },
    iconFor(item, selected) {
      const kind = iconKindFor(item)
      const url = primaryPhotoPinUrl(item)
      const hot = !selected && this.highlightedId != null && item.id === this.highlightedId
      return url ? photoIcon(kind, url, selected, hot) : this.pngIcons[kind]
    },
    latLngOf(c) {
      const lat = c && parseFloat(c.lat), lng = c && parseFloat(c.lng)
      return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : [41.8781136, -87.6297982]
    },
    emitBounds() {
      if (!this.map) return
      const b = this.map.getBounds()
      this.$emit("bounds-change", {
        south: b.getSouth(),
        west: b.getWest(),
        north: b.getNorth(),
        east: b.getEast(),
      })
    },
    refreshIcon(id) {
      const entry = id != null && this.markersById.get(id)
      if (entry) entry.marker.setIcon(this.iconFor(entry.item, this.art && this.art.id === id))
    },
    renderMarkers(items) {
      if (!this.cluster) return
      this.cluster.clearLayers()
      this.markersById.clear()

      const markers = []
      for (const item of items || []) {
        const lat = parseFloat(item.lat)
        const lng = parseFloat(item.lng)
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue

        const marker = L.marker([lat, lng], {
          icon: this.iconFor(item, this.art && this.art.id === item.id),
          // Read back by clusterIcon() to pick a photo for the bubble.
          item,
          // A pin click must not reach the map, whose click closes the panel.
          bubblingMouseEvents: false,
        })
        marker.on("click", () => this.clickOnArtMarker(item))
        marker.on("mouseover", () => this.$emit("marker-hover", item))
        marker.on("mouseout", () => this.$emit("marker-leave", item))
        markers.push(marker)
        this.markersById.set(item.id, { marker, item })
      }
      this.cluster.addLayers(markers)

      // The selected art may have been filtered away.
      if (this.art && !this.markersById.has(this.art.id)) this.close()
    },
    setSelected(item, selected) {
      const entry = item && this.markersById.get(item.id)
      if (entry) entry.marker.setIcon(this.iconFor(entry.item, selected))
    },
    clickOnArtMarker(art) {
      this.select(art)
      this.$emit("marker-click", art)
    },
    select(art) {
      this.setSelected(this.art, false)
      this.art = art
      this.nearby = nearbyTo(art, this.items)
      this.setSelected(art, true)
    },
    close() {
      this.setSelected(this.art, false)
      this.art = null
      this.nearby = []
    },
    goTo(item) {
      if (this.map) {
        this.map.flyTo(
          [parseFloat(item.lat), parseFloat(item.lng)],
          Math.max(this.map.getZoom(), NEARBY_ZOOM),
          { duration: 0.6 }
        )
      }
      this.select(item)
    },
    handleRedirect(id) {
      // If we are in an embed context, we might want to open in new tab?
      // For now, emit event so parent can decide, or default to router push
      this.$emit("art-click", id)
    },
    fitBounds(items) {
      const bounds = boundsFor(items)
      if (!bounds || !this.map) return
      // Leaflet fits against its last measured size; the container may have
      // just been shown or resized (phone list/map switch).
      this.map.invalidateSize()
      this.map.fitBounds(bounds, { maxZoom: MAX_FIT_ZOOM, padding: [20, 20] })
    },
    // The unfiltered view: Americas + Europe, see HOME_BOUNDS.
    fitHome() {
      if (!this.map) return
      this.map.invalidateSize()
      this.map.fitBounds(HOME_BOUNDS)
    },
  },
  watch: {
    labelsOn() {
      this.applyLabels()
    },
    // A host page may rewrite the `labels` attribute at any time -- the docs
    // promise every embed attribute is reactive, and the playground relies on
    // it. Treated as the new default rather than as the visitor's choice, so
    // it is applied but never written to storage (see toggleLabels).
    labels(value) {
      this.labelsOn = value
    },
    highlightedId(now, before) {
      this.refreshIcon(before)
      this.refreshIcon(now)
    },
    center(c) {
      if (this.map && c && Number.isFinite(parseFloat(c.lat))) this.map.setView(this.latLngOf(c), this.map.getZoom())
    },
    zoom(z) {
      if (this.map) this.map.setZoom(z)
    },
    // TEST SEAM -- read by test/e2e/*.spec.mjs, not by application code.
    //
    // Publishes the marker set this component is about to render, derived
    // exclusively from its own `items` prop. It never reads the map instance,
    // so it still works when the tile server is blocked, which is exactly how
    // the E2E suite runs: it aborts every off-origin request.
    //
    // Opt-in only: the harness sets window.__ARTAROUND_TEST_ENABLED__ through
    // page.addInitScript() before any page script runs, so this is inert in a
    // real browser. It only writes to window; no rendering behavior changes.
    items: {
      immediate: true,
      handler(items) {
        // Rendering first; the seam below is observation only. Before mount
        // there is no cluster layer yet, and mounted() renders on its own.
        this.renderMarkers(items)

        if (typeof window === "undefined") return
        if (!window.__ARTAROUND_TEST_ENABLED__) return

        window.__ARTAROUND_TEST__ = {
          markers: (items || []).map(item => ({
            id: item.id,
            lat: item.lat,
            lng: item.lng,
            archived: !!item.archived,
            // Deliberately the same lookups renderMarkers() uses, so this also
            // observes the runtime __webpack_public_path__ that
            // packs/map_widget.js rewrites. iconUrl is the PNG pin, which is
            // what renders whenever photoUrl is null.
            iconUrl: iconUrlFor(iconKindFor(item)),
            photoUrl: primaryPhotoUrl(item),
          })),
        }
      },
    },
  },
}
</script>

<style scoped>
.map-container {
  width: 100%;
  height: 100%;
  position: relative;
}
.map-canvas {
  width: 100%;
  height: 100%;
  background: #e9e4d6;
}

/* ---- detail panel ---------------------------------------------------- */
.art-panel {
  position: absolute;
  top: 12px;
  right: 12px;
  bottom: 12px;
  width: min(300px, calc(100% - 24px));
  z-index: 1000; /* above Leaflet's panes (400) and level with its controls */
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  transition: transform 0.22s ease, opacity 0.22s ease;
}
.art-panel--hidden {
  transform: translateX(calc(100% + 24px));
  opacity: 0;
  pointer-events: none;
}
.art-card {
  position: relative;
  flex: none;
}
.art-panel__close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  width: 28px;
  height: 28px;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 18px;
  line-height: 28px;
  text-align: center;
}
.art-meta div {
  display: flex;
  gap: 6px;
}
.art-meta dt {
  font-weight: 600;
  color: #12302e;
}
.art-meta dt::after {
  content: ":";
}
.art-panel__section {
  font-size: 12px;
  font-weight: 700;
  color: #12302e;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
  padding: 0 4px;
}
.art-nearby {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  text-align: left;
  font-size: 13px;
  line-height: 1.3;
}
.art-nearby:hover {
  background: #f2f2f2;
}
.art-nearby + .art-nearby {
  border-top: 1px solid #e5e7eb;
}
.art-nearby img,
.art-nearby__blank {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  object-fit: cover;
  background: #e5e7eb;
  flex: none;
}
.art-nearby__text {
  display: block;
  flex: 1;
}
.art-nearby__distance {
  display: block;
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}
</style>

<style>
/* Unscoped on purpose: Leaflet builds the pin DOM itself, so scoped selectors
   never match it. Namespaced with aa- to stay out of everyone else's way. */

/* ---- map chrome pills ------------------------------------------------- */
/* The street-labels and my-location toggles. Grouped rather than written twice
   because they stack against each other in the topleft corner: two separate
   copies of this would drift, and a pair of pills that are almost the same size
   reads worse than either alone.

   Deliberately not .leaflet-bar: that draws the chunky bordered stack of the
   zoom control, and these are preferences the visitor sets occasionally, not
   controls they work. A quiet pill in the same language as the detail panel's
   cards. */
.aa-labels-toggle,
.aa-locate-toggle {
  display: block;
  padding: 5px 11px;
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #12302e;
  font: 600 12px/1.2 Inter, "Open Sans", sans-serif;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}
.aa-labels-toggle:hover,
.aa-locate-toggle:hover {
  background: #f2f2f2;
}
/* The pressed state inverts fill and text rather than only tinting the
   background: over watercolor the button sits on a different colour every time
   the map moves, and a subtle tint is unreadable against half of them. The
   state a screen reader gets is aria-pressed, not this. */
.aa-labels-toggle--on {
  background: #12302e;
  border-color: #12302e;
  color: #fff;
}
.aa-labels-toggle--on:hover {
  background: #1c4340;
}
/* The one place the two pills part company. This one fills with the exact blue
   of the dot it just put on the map (USER_LOCATION_COLOR in basemap.js), so the
   button and the thing it produced are visibly the same object. Pressing a
   button and having something appear elsewhere on screen is a connection worth
   making in colour rather than leaving the visitor to infer. */
.aa-locate-toggle--on {
  background: #1a73e8;
  border-color: #1a73e8;
  color: #fff;
}
.aa-locate-toggle--on:hover {
  background: #1765cc;
}
/* Between the press and the first fix -- which under enableHighAccuracy is
   seconds, not milliseconds, and outdoors from cold can be most of the 15s
   timeout. Held at the pressed colour but breathing, because a button that
   looks settled while nothing appears on the map reads as broken. The title
   says the same thing in words for anyone who cannot see it pulse. */
.aa-locate-toggle--pending {
  animation: aa-locate-pulse 1.4s ease-in-out infinite;
}
@keyframes aa-locate-pulse {
  50% {
    opacity: 0.55;
  }
}
/* Respected rather than assumed: a pulsing control is exactly the kind of
   unprompted motion this setting exists to switch off, and the state is already
   carried by the title. */
@media (prefers-reduced-motion: reduce) {
  .aa-locate-toggle--pending {
    animation: none;
  }
}
/* A denial or a failed fix. Not red-on-white shouting -- nothing is broken, the
   map works exactly as well without a dot -- just enough that the button does
   not sit there looking like an ordinary off state after the visitor pressed
   it and nothing happened. The sentence itself is in the title. */
.aa-locate-toggle--error {
  border-color: #fe0205;
  color: #9b1c1f;
}
/* Leaflet gives its own controls no focus ring and the map is a dark, busy
   backdrop; without this the keyboard position is invisible on the pressed
   state, where the button is nearly the colour of an outline. */
.aa-labels-toggle:focus-visible,
.aa-locate-toggle:focus-visible {
  outline: 2px solid #fe0205;
  outline-offset: 2px;
}

/* The locate control's live region: announced, never seen.
   Clipped rather than `display: none` or `visibility: hidden`, both of which
   take the element out of the accessibility tree entirely and would make the
   announcement it exists for silent. 1px and not 0 for the same reason -- a
   zero-sized box is dropped by some readers. */
.aa-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.aa-pin {
  width: 44px;
  height: 44px;
  position: relative;
  cursor: pointer;
  --aa-ring: #fe0205;
}
.aa-pin--gray {
  --aa-ring: #9ca3af;
}
.aa-pin::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -5px;
  width: 10px;
  height: 10px;
  background: var(--aa-ring);
  transform: translateX(-50%) rotate(45deg);
  border-radius: 2px;
}
.aa-pin__inner {
  position: relative;
  z-index: 1;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: hidden;
  border: 2.5px solid var(--aa-ring);
  background: #f2f2f2;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.35);
  transition: transform 0.15s ease;
}
/* Leaflet's own `.leaflet-container .leaflet-marker-pane img { width: auto }`
   outranks a two-class selector, so this one carries the pane in its path. */
.leaflet-marker-pane .aa-pin__inner img {
  width: 44px;
  height: 44px;
  object-fit: cover;
  display: block;
}
.aa-pin--hot .aa-pin__inner {
  transform: scale(1.15);
  border-color: #12302e;
}
.aa-pin--selected .aa-pin__inner {
  transform: scale(1.3);
  border-color: #12302e;
}
.leaflet-marker-icon:has(.aa-pin--hot) {
  z-index: 9000 !important;
}
.leaflet-marker-icon:has(.aa-pin--selected) {
  z-index: 10000 !important;
}

/* ---- cluster bubbles ------------------------------------------------- */
.aa-cluster {
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: visible;
  cursor: pointer;
}
.leaflet-marker-pane .aa-cluster img {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  border: 2.5px solid #fff;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.35);
}
.aa-cluster--plain {
  background: #12302e;
  border: 2.5px solid #fff;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.35);
}
.aa-cluster__count {
  position: absolute;
  right: -6px;
  top: -6px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: #fe0205;
  color: #fff;
  font: 700 11px/22px Inter, "Open Sans", sans-serif;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}
.aa-cluster--plain .aa-cluster__count {
  position: static;
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border-radius: 50%;
  background: transparent;
  box-shadow: none;
  line-height: 43px;
  font-size: 13px;
}
</style>
